"use client";

import { useState, useEffect, useMemo } from "react";
import { CreateConnectorFn, useConnect, useDisconnect, type Connector } from "wagmi";
import useConnectors from "@shared/src/hooks/useConnectors";
import useAuth from "@shared/src/hooks/auth/useAuth";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { getErrorMessage } from "@shared/src/utils";
import { ixToast } from "@shared/src/utils/ixToast";
import { useModal } from "connectkit";
import { EIP1193Provider } from "viem";
// Define types for wallet info
export interface WalletInfo {
  id: string;
  name: string;
  icon: string;
  isInstalled?: boolean;
  provider?: any;
  uid?: string;
}

export function useWalletConnection() {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const connectors = useConnectors();
  const { authenticate } = useAuth();

  const {
    isVerifying,
    selectedView,
    setSelectedView,
    signOut: resetAll,
    setIsVerifying,
    setShowBackButton,
    resetEmail,
    goBack,
    setModalOpen,
    setWalletStatus,
    closeAndResetAuthModal,
  } = useAuthStore();
  const { connect, connectAsync, connectors: wgConnectors } = useConnect();
  const { disconnectAsync } = useDisconnect();

  // Get popular wallets
  // Handle wallet connection from Reown
  const handleReownWalletConnect = async (wallet: WalletInfo) => {
    try {
      console.log("Handling Reown wallet connection:", wallet);
      setSelectedView(wallet.id);
      // Reset wallet status before attempting connection
      useAuthStore.getState().setWalletStatus("initial", wallet.name || "Wallet");
      await authenticate({
        type: "wallet",
        connector: wallet as Connector,
        connectorIcon: wallet.icon || "/images/logos/WalletConnectLogo.svg",
        connectorId: wallet.id,
      });
    } catch (error: any) {
      console.log("Error connecting Reown wallet:", error);
      useAuthStore.getState().setWalletStatus("error", wallet.name || "Wallet");
      console.error("Error connecting Reown wallet:", error);
      ixToast.error(error.message || "Error connecting wallet");
    }
  };

  // Handle direct wallet connection
  const connectWallet = async (wallet: Connector) => {
    try {
      setSelectedView(wallet.id);
      // Reset wallet status before attempting connection
      useAuthStore.getState().setWalletStatus("initial", wallet.name || "Wallet");
      useAuthStore.setState({
        walletIcon: wallet.icon || "/images/logos/WalletConnectLogo.svg",
        walletName: wallet.name || "Wallet",
      });
      await authenticate({
        type: "wallet",
        connector: wallet,
        connectorIcon: wallet.icon || "/images/logos/WalletConnectLogo.svg",
        connectorId: wallet.id,
      });
    } catch (error: any) {
      useAuthStore.getState().setWalletStatus("error", wallet.name || "Wallet");
      console.error("Error connecting wallet:", error);
      ixToast.error(error.message || "Error connecting wallet");
    }
  };

  // Handle opening the Reown modal
  const handleAllWalletsClick = async () => {
    console.log("All Wallets button clicked");
    console.log("Opening AppKit with view: AllWallets", { connectors, wgConnectors });
    // Reset wallet status when opening all wallets
    useAuthStore.getState().setWalletStatus("initial", "Wallet");

    // setOpen(true);
    // openOnboarding();

    const wcc = wgConnectors.find(c => c.id === "walletConnect");
    if (wcc) {
      const res = await connectAsync({ connector: wcc });
      const provider = wcc.provider as EIP1193Provider;
      if (!provider) {
        throw new Error("WalletConnect connector not found");
      }
      await authenticate({
        type: "walletConnect",
        provider: provider,
        connectorIcon: "/images/logos/WalletConnectLogo.svg",
        connectorId: "walletConnect",
      });
    } else alert("no wallet connect");

    // await openAppKit({ view: "AllWallets" });
    // setModalOpen(false);
  };

  // Handle dialog close
  const handleClose = () => {
    resetAll();
    setSelectedView("social");
  };

  // Handle back navigation
  const handleBack = () => {
    // If we're in verification mode, check the context
    if (isVerifying) {
      setIsVerifying(false);
      setShowBackButton(false);
      resetEmail();
    } else {
      const currentView = selectedView;
      const currentWalletStatus = useAuthStore.getState().walletStatus;

      if (currentView === "social" && currentWalletStatus === "awaiting_confirmation") {
        useAuthStore.getState().setWalletStatus("initial", "");
      }

      goBack();
    }
    goBack();
  };

  // Handle image load errors
  const handleImageError = (walletId: string) => {
    setImageErrors(prev => ({ ...prev, [walletId]: true }));
  };

  const handleWalletConnectClick = async () => {
    console.log("wcc WalletConnect button clicked");
    setSelectedView("walletConnect");
    let retries = 0;
    // await new Promise(resolve => setTimeout(resolve, 1000));

    async function connectWithRetry(connector: Connector<CreateConnectorFn>) {
      try {
        const res = await connectAsync({ connector });
        const provider = await connector.getProvider();
        console.log("wcc provider", { provider, res });
        setModalOpen(true);
        return provider;
      } catch (error: any) {
        if (error?.message.includes("Connector already connected")) {
          console.log("walletconnect retrying", retries);
          if (retries >= 3) throw error;
          retries++;
          await disconnectAsync({ connector });
          return connectWithRetry(connector);
        }
      }
    }
    try {
      const wcc = wgConnectors.find(c => c.id === "walletConnect");

      if (wcc) {
        const provider = (await connectWithRetry(wcc)) as EIP1193Provider;
        setModalOpen(true);
        if (!provider) {
          throw new Error("WalletConnect connector not found");
        }

        await authenticate({
          type: "walletConnect",
          provider: provider,
          connectorIcon: "/images/logos/WalletConnectLogo.svg",
          connectorId: "walletConnect",
        });
      } else alert("no wallet connect");
    } catch (error: any) {
      console.log("walletconnect errormes", error?.toString());
      if (error?.toString()?.includes("Modal closed")) {
        useAuthStore.getState().closeAndResetAuthModal();
      } else {
        const message = getErrorMessage(error);
        setWalletStatus("error", "WalletConnect");
        if (message) {
          ixToast.error(message);
        }
      }
    }
  };

  // Reset wallet store when view changes
  // useEffect(() => {
  //   if (selectedView === "social" || selectedView === "all") {
  //     closeAndResetAuthModal();
  //   }
  // }, [selectedView, closeAndResetAuthModal]);

  return {
    connectors,
    selectedView,
    isVerifying,
    imageErrors,
    handleReownWalletConnect,
    connectWallet,
    handleWalletConnectClick,
    handleAllWalletsClick,
    handleClose,
    handleBack,
    handleImageError,
    setSelectedView,
  };
}
